import { useQuery } from "@tanstack/react-query";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { getQuiz, getQuizScores, type Quiz, type Score } from "@/lib/quiz-api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";

export default function QuizResults({ params }: { params: { internalId: string } }) {
  const { data: quiz, isLoading: isLoadingQuiz } = useQuery<Quiz>({
    queryKey: [`/api/quiz/${params.internalId}`],
    queryFn: () => getQuiz(params.internalId),
  });

  const { data: scores, isLoading: isLoadingScores } = useQuery<Score[]>({
    queryKey: [`/api/quiz/${params.internalId}/scores`],
    queryFn: () => getQuizScores(params.internalId),
    enabled: !!quiz,
  });

  if (isLoadingQuiz || isLoadingScores) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-32 bg-gray-100 rounded" />
        <div className="h-64 bg-gray-100 rounded" />
      </div>
    );
  }

  if (!quiz) {
    return null;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Statistics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-pink-500">{scores?.length ?? 0}</p>
              <p className="text-sm text-gray-500">Total Responses</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-pink-500">
                {scores?.length
                  ? Math.round(
                      (scores.reduce((acc, score) => acc + score.score, 0) /
                        (scores.length * quiz.questions.length)) *
                        100
                    )
                  : 0}
                %
              </p>
              <p className="text-sm text-gray-500">Average Score</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Responses</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Percentage</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scores?.map((score) => (
                  <TableRow key={score.id}>
                    <TableCell className="font-medium">{score.playerName}</TableCell>
                    <TableCell>
                      {score.score} / {quiz.questions.length}
                    </TableCell>
                    <TableCell>
                      {Math.round((score.score / quiz.questions.length) * 100)}%
                    </TableCell>
                    <TableCell>
                      {format(new Date(score.createdAt), "MMM d, yyyy h:mm a")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}